/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type AxiosInstance } from 'axios';
import type { SkuDealsResponse } from '../interface';
import type { SkuDealsFromAPI } from '@/types/availability';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { requestBaseURL } from '@/config/serviceAPI';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';
import { useQueries, type QueryClient } from '@tanstack/react-query';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';
import { type AcesDealsVehicleAttributes } from '@/features/deals';
import { getAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';
import { type VehicleSummary } from '@/types';

type ProductDealsQueryOptions = {
  dealsVehicleAttributes: AcesDealsVehicleAttributes | null;
  acesEnabled: boolean;
  skuIds: Array<string | null> | null;
  skuUniqueIds?: Array<{ skuId: string; uniqueId?: number }> | null;
  vehicleId: string | undefined;
  storeNumber: string | undefined;
};

const URL = `${requestBaseURL}/ecomm/b2c/browse/v3/deal/details`;
const ACES_URL = `${requestBaseURL}/ecomm/b2c/browse/v4/deal/details`;

const getProductSkuDeals = async (
  options: ProductDealsQueryOptions,
  axiosInstance?: AxiosInstance,
) => {
  const { skuIds, skuUniqueIds, acesEnabled, dealsVehicleAttributes } = options;
  const BASE_URL = acesEnabled ? ACES_URL : URL;

  if (!skuIds) {
    throw new Error('skuIds is required');
  }

  // For ACES enabled calls, use skuUniqueIds format if available
  let formattedSkuIds: string[];
  if (acesEnabled && skuUniqueIds && skuUniqueIds.length > 0) {
    formattedSkuIds = skuUniqueIds.map((item) =>
      item.uniqueId ? `${item.skuId}:${item.uniqueId}` : item.skuId,
    );
  } else {
    formattedSkuIds = skuIds.filter((id): id is string => id !== null);
  }

  const response = await getAxios(axiosInstance).get<SkuDealsResponse>(
    `${BASE_URL}/${formattedSkuIds.join(',')}`,
    {
      params: acesEnabled && dealsVehicleAttributes ? dealsVehicleAttributes : null,
    },
  );
  return response.data;
};

const {
  useData: useProdSkuDeals,
  prefetch: prefetchProdSkuDealsData,
  query: productSkuDealsQuery,
} = createQuery<SkuDealsFromAPI[], ProductDealsQueryOptions>('productSkuDeals', getProductSkuDeals);

export const useProductSkuDeals = ({
  skuIds,
  skuUniqueIds,
  enabled = true,
}: {
  skuIds: string[] | null | undefined;
  skuUniqueIds?: Array<{ skuId: string; uniqueId?: number }> | null;
  enabled?: boolean;
}) => {
  const { acesEnabled } = useAcesFlags();
  const headerResult = useHeaderData();
  const locale = useLocale();
  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();
  const storeNumber = headerResult.data?.storeNumber;
  const vehicleId = getPreferredVehicle(headerResult.data)?.catalogVehicleId;
  const isEnabled =
    locale !== countryCodes.ptBr && headerResult.isSuccess && enabled && Boolean(skuIds?.length);

  return useProdSkuDeals({
    acesEnabled,
    dealsVehicleAttributes,
    enabled: isEnabled,
    skuIds: skuIds?.length ? skuIds : null,
    skuUniqueIds,
    storeNumber: storeNumber ?? '',
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    vehicleId: vehicleId || '0',
    gcTime: 0,
  });
};

type MultipleSkuDealsOptions = {
  skuIds: string[][];
  skuUniqueIds?: Array<Array<{ skuId: string; uniqueId?: number }>>;
  enabled?: boolean;
};

export const useProductSkuDealsBatches = ({
  skuIds: multipleSkuIds,
  skuUniqueIds: multipleSkuUniqueIds,
  enabled = true,
}: MultipleSkuDealsOptions) => {
  const headerResult = useHeaderData();
  const { acesEnabled } = useAcesFlags();
  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();
  const storeNumber = headerResult.data?.storeNumber;
  const vehicleId = getPreferredVehicle(headerResult.data)?.catalogVehicleId;
  const locale = useLocale();

  const isEnabled = headerResult.isSuccess && enabled && locale !== countryCodes.ptBr;

  return useQueries({
    queries: multipleSkuIds.map((skuIds, index) => ({
      enabled: isEnabled && skuIds.length > 0,
      queryKey: productSkuDealsQuery.getFullKey({
        skuIds,
        skuUniqueIds: multipleSkuUniqueIds?.[index],
        storeNumber: storeNumber ?? '',
        vehicleId: vehicleId || '0',
        acesEnabled,
        dealsVehicleAttributes,
      }),
      // we should use the default `gcTime`
      queryFn: (ctx) => getProductSkuDeals(ctx.queryKey[1] as ProductDealsQueryOptions),
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        pending: results.some((result) => result.isPending),
        isFetching: results.some((result) => result.isFetching),
        combinedData: results.reduce<Record<string, SkuDealsFromAPI>>((acc, result) => {
          if (result.data) {
            for (const data of result.data) {
              acc[data.skuId] = data;
            }
          }

          return acc;
        }, {}),
      };
    },
  });
};

export const prefetchProductSkuDeals = async (
  options: Omit<ProductDealsQueryOptions, 'dealsVehicleAttributes'> & {
    preferredVehicle: VehicleSummary | undefined;
  },
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  const dealsVehicleAttributes =
    options.preferredVehicle && options.acesEnabled
      ? getAcesDealsVehicleAttributes(options.preferredVehicle, options.acesEnabled)
      : null;

  const optionsWithVehicleAttributes = {
    ...options,
    dealsVehicleAttributes,
  };

  return prefetchProdSkuDealsData(queryClient, optionsWithVehicleAttributes, axiosInstance);
};
